'use client';

import { FC, memo, useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { Stack } from '@chakra-ui/react';
import { motion } from 'framer-motion';

import { ScrollButton } from '@/components/common';
import { SuggestionItemGroup } from '@/components/features/Editor';
import { User } from '@/types/auth';
import {
  CHAT_MESSAGE_RATING,
  ChatMessage,
  LibraryReference,
} from '@/types/chat';

import {
  libraryReferencesFor,
  mergeReferences,
} from '../utils/libraryDocuments';
import { AssistantMessage } from './AssistantMessage';
import { BaseContainer } from './BaseContainer';
import { UserMessage } from './UserMessage';

interface Props {
  user: User | null;
  messages: ChatMessage[];
  sessionId?: string | null;
  onMessageRatingChange?: (
    messageId: string,
    rating: CHAT_MESSAGE_RATING
  ) => void;
  showFeedback?: boolean;
  className?: string;
  onLastMessageProcessCompleted?: () => void;
  possibleCommands?: SuggestionItemGroup[];
  isAutoScrollActive?: boolean;
  /** Connie's working panel on assistant turns (see AssistantMessage). */
  workingPanel?: boolean;
}

interface MessageRowProps {
  user: User;
  message: ChatMessage;
  isFirst: boolean;
  sessionId?: string | null;
  showFeedback: boolean;
  possibleCommands?: SuggestionItemGroup[];
  workingPanel?: boolean;
  sessionReferences?: LibraryReference[];
  onMessageRatingChange?: (
    messageId: string,
    rating: CHAT_MESSAGE_RATING
  ) => void;
  onProcessed: () => void;
  onProcessCompleted?: () => void;
}

// Memoised so a streamed token only re-renders the message it belongs to.
// Without this, every tick re-renders the whole thread, and the cost grows
// with each turn and each tool result already on screen.
const MessageRow = memo(function MessageRow({
  user,
  message,
  isFirst,
  sessionId,
  showFeedback,
  possibleCommands,
  workingPanel,
  sessionReferences,
  onMessageRatingChange,
  onProcessed,
  onProcessCompleted,
}: MessageRowProps) {
  const handleRatingChange = useCallback(
    (rating: CHAT_MESSAGE_RATING) =>
      onMessageRatingChange?.(message.id, rating),
    [message.id, onMessageRatingChange]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {message.type === 'bot' ? (
        <AssistantMessage
          message={message}
          sessionId={sessionId}
          workingPanel={workingPanel}
          sessionReferences={sessionReferences}
          onRatingChange={handleRatingChange}
          showFeedback={showFeedback}
          showMessageActions={!isFirst}
          onProcessed={onProcessed}
          onProcessCompleted={onProcessCompleted}
        />
      ) : (
        <UserMessage
          user={user}
          message={message}
          possibleCommands={possibleCommands}
        />
      )}
    </motion.div>
  );
});

// How close to the bottom still counts as "following the stream".
const BOTTOM_THRESHOLD_PX = 32;

const ChatBasic: FC<Props> = ({
  user,
  messages,
  sessionId,
  onMessageRatingChange,
  showFeedback = true,
  className,
  onLastMessageProcessCompleted,
  possibleCommands,
  isAutoScrollActive = true,
  workingPanel = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);

  // Every Library Document any turn of this chat touched, so a follow-up reply
  // can cite a document an earlier turn read. Keyed on a signature rather than
  // `messages`, which changes on every streamed token and would re-render every
  // memoised row.
  const referencesSignature = messages
    .map(
      message =>
        `${message.id}:${message.libraryReferences?.length ?? 0}:${message.toolCallOutputs?.length ?? 0}`
    )
    .join('|');
  const sessionReferences = useMemo(
    () =>
      messages
        .filter(message => message.type === 'bot')
        .reduce<
          LibraryReference[]
        >((all, message) => mergeReferences(all, libraryReferencesFor(message)), []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [referencesSignature]
  );

  const pinToBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight - container.clientHeight;
  }, []);

  // A ResizeObserver fires after layout but before paint, so the pin lands in
  // the same frame the new tokens are painted. Scrolling from an effect or a
  // rAF always trails the stream by a frame and leaves the tail below the fold.
  // It also covers growth React never announces: markdown reflow, syntax
  // highlighting, tool accordions, and the meta row added when a turn ends.
  useLayoutEffect(() => {
    if (!isAutoScrollActive) return;

    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const observer = new ResizeObserver(() => {
      if (!isPinnedRef.current) return;
      pinToBottom();
    });

    observer.observe(content);
    observer.observe(container);

    return () => observer.disconnect();
  }, [isAutoScrollActive, pinToBottom]);

  // Following stops only when the reader ends up away from the bottom. Pins
  // always land at zero distance, so they re-arm this instead of cancelling it.
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    isPinnedRef.current =
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      BOTTOM_THRESHOLD_PX;
  }, []);

  const handleProcessed = useCallback(() => {
    if (!isAutoScrollActive || !isPinnedRef.current) return;
    pinToBottom();
  }, [isAutoScrollActive, pinToBottom]);

  if (!user) {
    return null;
  }

  return (
    <Stack position='relative' flex={1} minH={0} minW={0} className={className}>
      <BaseContainer
        className='h-full min-h-0 min-w-0 px-4 pt-4 pb-4'
        contentClassName='space-y-4'
        scrollToRef={bottomRef}
        contentRef={contentRef}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {messages?.map((message, index) => (
          <MessageRow
            key={message.id}
            user={user}
            message={message}
            isFirst={index === 0}
            sessionId={sessionId}
            showFeedback={showFeedback}
            possibleCommands={possibleCommands}
            workingPanel={workingPanel}
            sessionReferences={sessionReferences}
            onMessageRatingChange={onMessageRatingChange}
            onProcessed={handleProcessed}
            onProcessCompleted={onLastMessageProcessCompleted}
          />
        ))}
      </BaseContainer>

      <div className='absolute right-7 bottom-4 z-30'>
        <ScrollButton
          variant='glassy'
          containerRef={containerRef}
          className='shadow-sm'
          colorScheme='gray'
        />
      </div>
    </Stack>
  );
};

export { ChatBasic as ChatContainer };
